import React, { useState, useEffect, useRef } from 'react';
import './Enquiryoperations.css';
import { X } from 'lucide-react';
import API_ENDPOINTS from '../../../../config/apiEndpoints';

const Enquiryoperations = ({ 
    isOpen, 
    onClose, 
    leadId 
}) => {
    const TOKEN = '-2295521862261168';
    const SERVICE_AZURE_URL = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
    const LoggedUserId = localStorage.getItem('UserId') || '34594';

    const Get_Call_Schedule_Action = `${SERVICE_AZURE_URL}/${API_ENDPOINTS.ENQUIRIES.GET_SCHEDULE_CALL_ACTION}`;
    const Get_Mobile_Number = `${SERVICE_AZURE_URL}/${API_ENDPOINTS.ENQUIRIES.GET_MOBILE_NUMBER}`;
    const Update_Dialer_Data = `${SERVICE_AZURE_URL}/${API_ENDPOINTS.ENQUIRIES.UPDATE_DIALER_DATA || 'Update_Dialer_Data'}`;

    const [labels, setLabels] = useState([]);
    const [mobileNumbers, setMobileNumbers] = useState([]);
    const [selectedNumber, setSelectedNumber] = useState('');
    const [selectedLabel, setSelectedLabel] = useState('');
    const [scheduleTime, setScheduleTime] = useState('');
    const [loading, setLoading] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const dropdownRef = useRef(null);

    const robustParse = (val) => {
        if (typeof val === 'string') {
            try { return JSON.parse(val); } catch (e) { return val; }
        }
        return val;
    };

    const safeParse = (data) => {
        if (!data) return null;
        let current = data.d !== undefined ? robustParse(data.d) : data;
        let changed = true;
        while (changed && current && typeof current === 'object' && !Array.isArray(current)) {
            changed = false;
            const keys = ['Details', 'Text', 'text', 'Data', 'Table'];
            for (const key of keys) {
                if (current[key] !== undefined) {
                    current = robustParse(current[key]);
                    changed = true;
                    break; 
                }
            }
        }
        return current;
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    useEffect(() => {
        if (isOpen && leadId) {
            fetchInitialData();
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            setScheduleTime(`${year}-${month}-${day}T${hours}:${minutes}`);
        }
    }, [isOpen, leadId]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [labelRes, mobileRes] = await Promise.all([
                fetch(Get_Call_Schedule_Action, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        Token: TOKEN,
                        LoggedUserId: String(LoggedUserId),
                        Details: { Mode: "SELECT", Id: 0, UserId: String(LoggedUserId) }
                    })
                }),
                fetch(Get_Mobile_Number, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        Token: TOKEN,
                        LoggedUserId: String(LoggedUserId),
                        Details: { 
                            EntityName: "enquiry", 
                            EntityId: String(leadId), 
                            UserId: String(LoggedUserId) 
                        }
                    })
                })
            ]);

            if (labelRes.ok) {
                const dataLabel = await labelRes.json();
                const parsedLabel = safeParse(dataLabel);
                const list = parsedLabel?.leftTabs || parsedLabel?.Labels || (Array.isArray(parsedLabel) ? parsedLabel : []);
                // Standardize based on user request "Final Working Code"
                if (Array.isArray(list)) {
                    setLabels(list.map(tab => ({
                        value: tab.name || tab.Id || '',
                        label: tab.text || tab.Value || tab.LabelName || tab.Name || ''
                    })));
                } else {
                    setLabels([]);
                }
            }

            if (mobileRes.ok) {
                const dataMobile = await mobileRes.json();
                const parsedMobile = safeParse(dataMobile);
                const numbers = parsedMobile?.MobileNos || (Array.isArray(parsedMobile) ? parsedMobile : []);
                setMobileNumbers(Array.isArray(numbers) ? numbers : []);
                if (numbers.length > 0) setSelectedNumber(numbers[0].MobileNo || numbers[0].Mobile || (typeof numbers[0] == 'string' ? numbers[0] : ''));
            }
        } catch (error) {
            console.error("Error fetching schedule call data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        // Date Validation: Current or next to current
        const dateObj = new Date(scheduleTime);
        const now = new Date();
        if (dateObj <= now) {
            alert("Please select a future date and time.");
            return;
        }

        setLoading(true);
        try {
            // Format time: YYYY-MM-DDTHH:mm -> DD-MMM-YYYY HH:mm
            const dateObj = new Date(scheduleTime);
            const day = String(dateObj.getDate()).padStart(2, '0');
            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const month = monthNames[dateObj.getMonth()];
            const year = dateObj.getFullYear();
            const hours = String(dateObj.getHours()).padStart(2, '0');
            const minutes = String(dateObj.getMinutes()).padStart(2, '0');
            const formattedTime = `${day}-${month}-${year} ${hours}:${minutes}`;

            // Safe Matching: l.name?.trim().toLowerCase() === selectedLabel?.trim().toLowerCase()
            const labelObj = labels.find(l => String(l.value || '').trim().toLowerCase() === String(selectedLabel || '').trim().toLowerCase());
            const labelName = labelObj?.value || selectedLabel;

            const operationJSON = JSON.stringify({
                LabelName: String(labelName),
                ScheduleTime: formattedTime,
                EntityName: "enquiry",
                EntityId: String(leadId),
                SuccessMessage: "Data saved successfully",
                MobileNo: String(selectedNumber)
            });

            const response = await fetch(Update_Dialer_Data, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Token: TOKEN,
                    LoggedUserId: String(LoggedUserId),
                    Details: {
                        UserId: String(LoggedUserId),
                        Mode: "NEW_RECORD",
                        OperationJSON: operationJSON
                    }
                })
            });

            const dataSave = await response.json();
            const parsedSave = safeParse(dataSave);
            
            if (response.ok && (parsedSave?.Code === 'Success' || parsedSave?.Status === 1 || dataSave?.Status === 1)) {
                setShowSuccess(true);
            } else {
                alert(parsedSave?.Message || dataSave?.Message || "Failed to save schedule call.");
            }
        } catch (error) {
            console.error("Error saving schedule call:", error);
            alert("An error occurred while saving.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="enquiry-op-modal-overlay">
            <div className="enquiry-op-modal-container">
                <div className="enquiry-op-modal-header">
                    <h3>Schedule Call Information</h3>
                    <button className="close-btn" onClick={onClose}><X size={20} /></button>
                </div>
                <div className="enquiry-op-modal-body">
                    {showSuccess ? (
                        <div className="success-modal-render">
                            <div className="success-icon-circle">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                            </div>
                            <h2 className="success-msg-title">Success</h2>
                            <p className="success-msg-desc">Data saved successfully.</p>
                            <button className="success-ok-btn" onClick={onClose}>OK</button>
                        </div>
                    ) : loading && labels.length === 0 ? (
                        <div className="loading-state">Loading...</div>
                    ) : (
                        <>
                            <div className="numbers-list">
                                {Array.isArray(mobileNumbers) && mobileNumbers.map((num, idx) => (
                                    <label key={idx} className="number-item">
                                        <input 
                                            type="radio" 
                                            name="mobileNumber" 
                                            value={num.MobileNo || num.Mobile || num} 
                                            checked={selectedNumber === (num.MobileNo || num.Mobile || num)}
                                            onChange={(e) => setSelectedNumber(e.target.value)}
                                        />
                                        <span className="number-text">{num.MobileNo || num.Mobile || num}</span>
                                    </label>
                                ))}
                                {(!Array.isArray(mobileNumbers) || mobileNumbers.length === 0) && <div className="no-data">No mobile numbers found.</div>}
                            </div>
                            <div className="form-group">
                                <label>Label Name</label>
                                <div className="custom-select-container" ref={dropdownRef}>
                                    <div 
                                        className={`select-selected ${isDropdownOpen ? 'active' : ''}`}
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    >
                                        {selectedLabel ? (labels.find(l => l.value === selectedLabel)?.label || selectedLabel) : 'Select Label Name'}
                                        <span className={`arrow ${isDropdownOpen ? 'up' : 'down'}`}></span>
                                    </div>
                                    {isDropdownOpen && (
                                        <div className="select-items">
                                            <div className="select-search-container">
                                                <input 
                                                    type="text" 
                                                    placeholder="Search..." 
                                                    className="select-search-input"
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="select-list-scroll">
                                                {labels
                                                    .filter(l => (l.label || '').toLowerCase().includes(searchTerm.toLowerCase()))
                                                    .map((l, i) => (
                                                        <div 
                                                            key={l.value.replace(/\s/g, "_") || i} 
                                                            className="select-item"
                                                            onClick={() => {
                                                                setSelectedLabel(l.value);
                                                                setIsDropdownOpen(false);
                                                                setSearchTerm('');
                                                            }}
                                                        >
                                                            {l.label}
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Schedule Time*</label>
                                <input 
                                    type="datetime-local" 
                                    className="op-input"
                                    value={scheduleTime}
                                    onChange={(e) => setScheduleTime(e.target.value)}
                                />
                            </div>
                        </>
                    )}
                </div>
                {!showSuccess && (
                    <div className="enquiry-op-modal-footer">
                        <button className="op-cancel-btn" onClick={onClose}>Cancel</button>
                        <button className="op-save-btn" disabled={loading || !selectedNumber} onClick={handleSave}>
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Enquiryoperations;
